import {
  genErrorListByInputColumn,
  mergeCells,
} from "./ClaimsTransformerSupport";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import * as strings from "common/strings";
import Button from "components/common/Button";
import { useState, useEffect } from "react";

const WarningTable = ({ errors, setDateFormat, dateConversionTypeList }) => {
  if (!errors?.warnings) return null;
  const hasMmDd = errors.warnings.some(
    (w) => w.code === "DATES_CONVERTED_MM_DD_YYYY"
  );
  const hasDdMm = errors.warnings.some(
    (w) => w.code === "DATES_CONVERTED_DD_MM_YYYY"
  );
  const hasIndeterminate = errors.warnings.some(
    (w) => w.code === "DATE_STRING_TYPE_INDETERMINATE"
  );

  if (hasDdMm) {
    setDateFormat(errors.column, "DDMM");
  } else if (hasMmDd) {
    setDateFormat(errors.column, "MMDD");
  }

  return (
    <Paper elevation={0} sx={{ marginBottom: "0.5rem", padding: "0.5rem" }}>
      {
        <table>
          <tbody>
            {hasIndeterminate && (
              <tr>
                <td>
                  <h2>{"Please select a date format"}</h2>
                </td>
                <td rowSpan={errors.warnings.length}>
                  <Select
                    value={
                      dateConversionTypeList?.[errors.column] ?? "ISO_ONLY"
                    }
                    onChange={(val) => {
                      setDateFormat(errors.column, val?.target?.value);
                    }}
                  >
                    <MenuItem value={"ISO_ONLY"}>{"Automatic"}</MenuItem>
                    <MenuItem value={"MMDD"}>{"Month/Day/Year"}</MenuItem>
                    <MenuItem value={"DDMM"}>{"Day/Month/Year"}</MenuItem>
                  </Select>
                </td>
              </tr>
            )}
            {errors.warnings.map((w) => (
              <tr>
                <td>
                  <Typography variant={"description"}>{w.message}</Typography>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    </Paper>
  );
};

const ErrorsTable = ({
  errors,
  substitutions,
  rowDeletions,
  onSubstitutionsChanged,
  onRowDeletionsChanged,
  onReProcess,
}) => {
  const theme = useTheme();
  const isDateErrorContext = (errors?.entries?.[0]?.errorCode ?? []).includes(
    "TO_DATE"
  );

  const parse = (value) =>
    isDateErrorContext
      ? value
      : (numbers.parseNumberString(value) ?? "").toString();

  const isValid = (value) =>
    isDateErrorContext
      ? dates.parseDateStringFromDateOrYear(value, {}) != null
      : !isNaN(Number(value));

  const updateMeta = (col, row, newValue) => {
    const parsed = parse(newValue);
    newValue = parsed === "" ? newValue : parsed;

    const item = {
      column: col,
      row: row,
      value: newValue,
      isValid: isValid(newValue),
    };
    const index =
      substitutions?.findIndex((x) => x.column === col && x.row === row) ?? -1;

    if (index === -1) {
      onSubstitutionsChanged([...substitutions, item]);
    } else {
      const updated = [...substitutions];
      updated.splice(index, 1);
      if (strings.hasValue(newValue)) {
        updated.push(item);
      }
      onSubstitutionsChanged(updated);
    }
  };

  const updateRowDeletions = (row, isIgnored) => {
    const inList = (rowDeletions ?? []).includes(row);
    if (isIgnored && !inList) {
      onRowDeletionsChanged([...(rowDeletions ?? []), row]);
    } else if (!isIgnored && inList) {
      onRowDeletionsChanged(rowDeletions?.filter((x) => x !== row) ?? []);
    }
  };

  const handleIgnoreAll = (ignoreAll) => {
    if (ignoreAll) {
      const allRows = errors?.entries.map((error) => error.row) || [];
      const uniqueRows = [...new Set([...(rowDeletions || []), ...allRows])];
      onRowDeletionsChanged(uniqueRows);
    } else {
      const currentErrorRows = errors?.entries.map((error) => error.row) || [];
      const remaining = (rowDeletions || []).filter(
        (row) => !currentErrorRows.includes(row)
      );
      onRowDeletionsChanged(remaining);
    }
  };

  const errorsWithSubstitutions = errors?.entries.map((error) => {
    const overlay =
      substitutions?.filter(
        (x) => x.column === error.column && x.row === error.row
      )?.[0] ?? {};
    return {
      ...error,
      ...overlay,
    };
  });

  const allCurrentRowsIgnored = errors?.entries.every((error) =>
    (rowDeletions || []).includes(error.row)
  );

  return (
    <Paper elevation={0}>
      <TableContainer sx={{ overflowX: "initial" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{"Details"}</TableCell>
              <TableCell>{"Cell"}</TableCell>
              <TableCell>{"Spreadsheet Value"}</TableCell>
              <TableCell>{"Override"}</TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Switch
                      checked={allCurrentRowsIgnored}
                      onChange={(e) => handleIgnoreAll(e.target.checked)}
                      size={"small"}
                    />
                  }
                  label={"Ignore all"}
                  labelPlacement={"start"}
                />
              </TableCell>
              <TableCell>
                <Button onClick={onReProcess}>{"Apply Changes"}</Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errorsWithSubstitutions.map((error) => {
              const isIgnored = (rowDeletions ?? []).includes(error.row);
              return (
                <TableRow key={errors.colChar + error.rowNumber}>
                  <TableCell key={"error"}>{error.error.join("; ")}</TableCell>
                  <TableCell key={"Cell"}>
                    {errors.colChar}
                    {error.rowNumber}
                  </TableCell>
                  <TableCell
                    key={"value"}
                    sx={{
                      color: isIgnored ? "silver" : null,
                      textDecoration: isIgnored ? "line-through" : null,
                    }}
                  >
                    {error.originalValue}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size={"small"}
                      value={error.value}
                      placeholder={isDateErrorContext ? "YYYY-MM-DD" : null}
                      error={!(error.isValid ?? true)}
                      onChange={(e) => {
                        const newValue = isDateErrorContext
                          ? dates.handleDateInput(e.target.value)
                          : e.target.value;
                        updateMeta(error.column, error.row, newValue);
                      }}
                      disabled={isIgnored}
                      InputProps={{
                        sx: {
                          background: isIgnored ? theme.brand.gray[500] : null,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isIgnored}
                          tabIndex={-1}
                          onChange={(e) =>
                            updateRowDeletions(error.row, e.target.checked)
                          }
                        />
                      }
                      label={"Ignore this row"}
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const DataValidation = ({
  conversionErrors,
  headings,
  origin,
  substitutions,
  rowDeletions,
  setDateConversionType,
  dateConversionTypeList,
  onReProcess,
  onSubstitutionsChanged,
  onRowDeletionsChanged,
  isMultiSheet = false,
  selectedSheets = [],
  sheetConfigs = {},
}) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const [independentTabIndex, setIndependentTabIndex] = useState(0);

  useEffect(() => {
    if (
      isMultiSheet &&
      selectedSheets.length > 1 &&
      independentTabIndex >= selectedSheets.length
    ) {
      setIndependentTabIndex(0);
    }
  }, [isMultiSheet, selectedSheets, independentTabIndex]);

  const getSelectedSheetData = () => {
    if (!isMultiSheet || selectedSheets.length <= 1) {
      return {
        substitutions,
        rowDeletions,
        dateConversionTypeList,
        headings,
        origin,
      };
    }

    const selectedSheetName = selectedSheets[independentTabIndex];
    const sheetConfig = sheetConfigs[selectedSheetName];

    return {
      substitutions: sheetConfig?.substitutions || [],
      rowDeletions: sheetConfig?.rowDeletions || [],
      dateConversionTypeList: sheetConfig?.dateConversionTypes || {},
      headings: headings,
      origin: sheetConfig?.origin || origin,
    };
  };

  const selectedSheetData = getSelectedSheetData();

  const handleSubstitutionsChange = (newSubstitutions) => {
    if (isMultiSheet && selectedSheets.length > 1) {
      const selectedSheetName = selectedSheets[independentTabIndex];
      onSubstitutionsChanged(newSubstitutions, selectedSheetName);
    } else {
      onSubstitutionsChanged(newSubstitutions);
    }
  };

  const handleRowDeletionsChange = (newRowDeletions) => {
    if (isMultiSheet && selectedSheets.length > 1) {
      const selectedSheetName = selectedSheets[independentTabIndex];
      onRowDeletionsChanged(newRowDeletions, selectedSheetName);
    } else {
      onRowDeletionsChanged(newRowDeletions);
    }
  };

  const handleDateConversionChange = (column, type) => {
    if (isMultiSheet && selectedSheets.length > 1) {
      const selectedSheetName = selectedSheets[independentTabIndex];
      setDateConversionType(column, type, selectedSheetName);
    } else {
      setDateConversionType(column, type);
    }
  };

  const errorsByInputColumn = mergeCells(
    genErrorListByInputColumn(
      conversionErrors,
      selectedSheetData.headings,
      selectedSheetData.origin
    ),
    selectedSheetData.origin
  );
  const errors = errorsByInputColumn?.[activeIndex];

  return (
    <Stack direction={"column"} spacing={2}>
      {isMultiSheet && selectedSheets.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={independentTabIndex}
            onChange={(event, newValue) => {
              setIndependentTabIndex(newValue);
            }}
            variant={"scrollable"}
            scrollButtons={"auto"}
          >
            {selectedSheets.map((sheetName, index) => (
              <Tab
                key={sheetName}
                label={sheetName}
                id={`sheet-tab-${index}`}
                aria-controls={`sheet-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {(errorsByInputColumn?.length ?? 0) > 0 && (
        <Stack direction={"column"} spacing={1}>
          <Typography>
            {"Marmalade encountered the following problems during import:"}
          </Typography>
          <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
            {errorsByInputColumn.map((error, index) => {
              return (
                <Card
                  variant={"outlined"}
                  key={index}
                  className={index === activeIndex ? "active" : null}
                  sx={{
                    width: "33%",
                    maxWidth: "14rem",
                    "&.active": {
                      borderColor: theme.brand.accent[500],
                      background: theme.brand.accent[200],
                    },
                  }}
                >
                  <CardActionArea
                    sx={{ height: "100%" }}
                    onClick={() => setActiveIndex(index)}
                  >
                    <CardContent>
                      <Typography fontWeight={"bold"}>
                        {error.inputColumnName}
                      </Typography>
                      <small>
                        {error.entries.length}
                        {error.entries.length === 1 ? " issue" : " issues"}
                      </small>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
          <Stack>
            <WarningTable
              errors={errors}
              dateConversionTypeList={selectedSheetData.dateConversionTypeList}
              setDateFormat={handleDateConversionChange}
            />
            <ErrorsTable
              errors={errors}
              substitutions={selectedSheetData.substitutions}
              rowDeletions={selectedSheetData.rowDeletions}
              onSubstitutionsChanged={handleSubstitutionsChange}
              onRowDeletionsChanged={handleRowDeletionsChange}
              onReProcess={onReProcess}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default DataValidation;
